import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../../firebase';
import './FeedbackWidget.css';

const FEEDBACK_TYPES = [
  { value: 'suggestion', label: 'Suggestion / idea' },
  { value: 'general', label: 'General feedback' },
];

const FAB_CLEAR_GAP_PX = 30;

export default function FeedbackWidget({ user = null }) {
  const location = useLocation();
  const hideOnStudyModule = location.pathname.startsWith('/study-module/');

  const triggerRef = useRef(null);
  const naturalBottomRef = useRef(24);

  const [isOpen, setIsOpen] = useState(false);
  /** When set, inline `bottom` lifts the fixed trigger above the footer social/feedback row. */
  const [triggerBottomPx, setTriggerBottomPx] = useState(null);
  const [type, setType] = useState('general');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMessage, setErrorMessage] = useState('');

  const firebaseReady = isFirebaseConfigured() && db;

  useEffect(() => {
    if (hideOnStudyModule) {
      setTriggerBottomPx(null);
      return undefined;
    }

    const updateFabDock = () => {
      const trigger = triggerRef.current;
      const anchor = document.getElementById('footer-fab-anchor');
      if (!trigger || !anchor) {
        setTriggerBottomPx(null);
        return;
      }

      let naturalBottomPx = naturalBottomRef.current;
      if (!trigger.style.bottom) {
        naturalBottomPx = parseFloat(getComputedStyle(trigger).bottom) || naturalBottomPx;
        naturalBottomRef.current = naturalBottomPx;
      }

      const h = trigger.offsetHeight;
      const r = anchor.getBoundingClientRect();
      const ih = window.innerHeight;
      const fabTop = ih - naturalBottomPx - h;
      const fabBottomEdge = ih - naturalBottomPx;
      const overlaps = fabTop < r.bottom && fabBottomEdge > r.top;

      if (!overlaps) {
        setTriggerBottomPx(null);
        return;
      }
      setTriggerBottomPx(Math.max(naturalBottomPx, ih - r.top + FAB_CLEAR_GAP_PX));
    };

    updateFabDock();
    window.addEventListener('scroll', updateFabDock, { passive: true });
    window.addEventListener('resize', updateFabDock);
    return () => {
      window.removeEventListener('scroll', updateFabDock);
      window.removeEventListener('resize', updateFabDock);
    };
  }, [hideOnStudyModule]);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => {
    if (status !== 'sending') {
      setIsOpen(false);
      setStatus('idle');
      setErrorMessage('');
      setMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setStatus('sending');
    setErrorMessage('');

    const payload = {
      type,
      message: message.trim(),
      email: user?.email?.trim() || null,
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      createdAt: new Date().toISOString(),
      userId: user?.uid || null,
    };

    try {
      if (firebaseReady) {
        await addDoc(collection(db, 'feedback'), payload);
        setStatus('success');
        setMessage('');
        setTimeout(handleClose, 1500);
      } else {
        setStatus('error');
        setErrorMessage('Feedback storage is not configured. Please share your feedback via email.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message || 'Could not send feedback. Please try again or email us.');
    }
  };

  if (hideOnStudyModule) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="feedback-widget-trigger"
        onClick={handleOpen}
        aria-label="Send feedback"
        title="Send feedback"
        style={triggerBottomPx != null ? { bottom: triggerBottomPx } : undefined}
      >
        <span className="feedback-widget-trigger-icon" aria-hidden="true">
          💬
        </span>
        <span className="feedback-widget-trigger-text">Feedback</span>
      </button>

      {isOpen && (
        <div
          className="feedback-widget-overlay"
          onClick={handleClose}
          onKeyDown={(e) => e.key === 'Escape' && handleClose()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-modal-title"
          aria-describedby="feedback-modal-desc"
        >
          <div
            className="feedback-widget-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="feedback-widget-modal-header">
              <h2 id="feedback-modal-title">Send feedback</h2>
              <button
                type="button"
                className="feedback-widget-modal-close"
                onClick={handleClose}
                aria-label="Close feedback"
              >
                ×
              </button>
            </div>
            <p id="feedback-modal-desc" className="feedback-widget-modal-desc">
              Your feedback helps us improve the learning platform. Tell us what you think.
            </p>

            <form onSubmit={handleSubmit} className="feedback-widget-form">
              <label htmlFor="feedback-type" className="feedback-widget-label">
                Type
              </label>
              <select
                id="feedback-type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="feedback-widget-select"
                disabled={status === 'sending'}
              >
                {FEEDBACK_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <label htmlFor="feedback-message" className="feedback-widget-label">
                Message <span className="feedback-widget-required">*</span>
              </label>
              <textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe your feedback..."
                rows={8}
                required
                className="feedback-widget-textarea"
                disabled={status === 'sending'}
            />

              {status === 'error' && errorMessage && (
                <p className="feedback-widget-error" role="alert">
                  {errorMessage}
                </p>
              )}
              {status === 'success' && (
                <p className="feedback-widget-success" role="status">
                  Thank you! Your feedback has been sent.
                </p>
              )}

              <div className="feedback-widget-actions">
                <button
                  type="button"
                  className="feedback-widget-btn feedback-widget-btn-secondary"
                  onClick={handleClose}
                  disabled={status === 'sending'}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="feedback-widget-btn feedback-widget-btn-primary"
                  disabled={status === 'sending' || !message.trim()}
                >
                  {status === 'sending' ? 'Sending…' : 'Send feedback'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
